"use client";

import { useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { toastApiError } from "@/lib/api-toast";
import { cancelAuction, deleteAuction } from "../../api";
export type OwnerAction = "delete" | "cancel" | null;

/**
 * The owner's cancel / delete — AuctionOwnerActions' flow, moved here
 * unchanged: a confirm dialog first; delete goes back to the owner's
 * auctions, cancel re-reads the page. (Errors surface through the app's
 * axios toast, as before; the catch only keeps a failure from escaping the
 * click.) One request at a time, the dialog held open while it is out, and a
 * deleted auction stays «busy» until the page has left it.
 */
export function useAuctionOwner({
  auctionId,
  lang
}: {
  auctionId: number;
  lang: "fa" | "en";
}) {
  const router = useRouter();
  const isFa = lang === "fa";
  const [pendingAction, setPendingAction] = useState<OwnerAction>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inFlight = useRef(false);
  const confirm = async () => {
    if (!pendingAction || inFlight.current) return;
    inFlight.current = true;
    setIsSubmitting(true);
    let leaving = false;
    try {
      if (pendingAction === "delete") {
        await deleteAuction(auctionId);
        toast.success(isFa ? "مزایده حذف شد" : "Auction deleted");
        leaving = true;
        router.push(`/${lang}/auctions`);
        return;
      }
      await cancelAuction(auctionId);
      toast.success(isFa ? "مزایده لغو شد" : "Auction canceled");
      setPendingAction(null);
      router.refresh();
    } catch (error) {
      toastApiError(error, isFa ? "انجام نشد؛ دوباره امتحان کنید." : "That didn't go through — try again.");
    } finally {
      if (!leaving) {
        inFlight.current = false;
        setIsSubmitting(false);
      }
    }
  };
  const close = () => {
    if (!inFlight.current) setPendingAction(null);
  };
  return {
    pendingAction,
    ask: setPendingAction,
    close,
    confirm,
    isSubmitting
  };
}